<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import MultiSelect from 'dashboard/components-next/filter/inputs/MultiSelect.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import BroadcastResolutionResults from './BroadcastResolutionResults.vue';

const props = defineProps({
  source: {
    type: Object,
    required: true,
  },
  teams: {
    type: Array,
    default: () => [],
  },
  inboxSelected: {
    type: Boolean,
    default: false,
  },
  isFirst: {
    type: Boolean,
    default: false,
  },
  isLast: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update', 'remove', 'moveUp', 'moveDown', 'confirm']);
const { t } = useI18n();

function updateField(field, value, clearResolution = false) {
  emit('update', {
    ...props.source,
    [field]: value,
    ...(clearResolution && props.source.type === 'identifiers'
      ? { resolution: null, confirmedSignature: '' }
      : {}),
  });
}

const selectedTeams = computed({
  get: () => props.teams.filter(team => props.source.teamIds.includes(team.id)),
  set: selected => {
    updateField(
      'teamIds',
      selected.map(team => team.id),
      true
    );
  },
});

const sourceTitle = computed(() =>
  props.source.type === 'team'
    ? t('BROADCASTS.COMPOSER.SOURCE.TEAM_TITLE')
    : t('BROADCASTS.COMPOSER.SOURCE.IDENTIFIERS_TITLE')
);

const sourceDescription = computed(() =>
  props.source.type === 'team'
    ? t('BROADCASTS.COMPOSER.SOURCE.TEAM_HELP')
    : t('BROADCASTS.COMPOSER.SOURCE.IDENTIFIERS_HELP')
);

const hasIdentifierValues = computed(() =>
  props.source.rawText?.split(/\r\n|\n|\r/).some(value => value.trim())
);
</script>

<template>
  <div
    class="flex flex-col gap-4 rounded-xl border border-n-strong bg-n-solid-2 p-4"
  >
    <div class="flex flex-wrap items-center gap-2">
      <div class="mr-auto flex items-center gap-2">
        <span class="i-lucide-grip-vertical size-4 text-n-slate-10" />
        <div>
          <h4 class="text-sm font-medium text-n-slate-12">
            {{ sourceTitle }}
          </h4>
          <p class="mt-1 text-xs font-normal text-n-slate-10">
            {{ sourceDescription }}
          </p>
        </div>
      </div>
      <Button
        type="button"
        size="xs"
        color="slate"
        variant="ghost"
        icon="i-lucide-arrow-up"
        :disabled="isFirst"
        :aria-label="t('BROADCASTS.COMPOSER.SOURCE.MOVE_UP')"
        @click="emit('moveUp')"
      />
      <Button
        type="button"
        size="xs"
        color="slate"
        variant="ghost"
        icon="i-lucide-arrow-down"
        :disabled="isLast"
        :aria-label="t('BROADCASTS.COMPOSER.SOURCE.MOVE_DOWN')"
        @click="emit('moveDown')"
      />
      <Button
        type="button"
        size="xs"
        color="ruby"
        variant="ghost"
        icon="i-lucide-trash-2"
        :aria-label="t('BROADCASTS.COMPOSER.SOURCE.REMOVE')"
        @click="emit('remove')"
      />
    </div>

    <div class="grid gap-4 sm:grid-cols-2">
      <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
        {{ t('BROADCASTS.COMPOSER.SOURCE.OPERATION') }}
        <select
          :value="source.operation"
          class="h-10 rounded-lg border border-n-weak bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
          @change="updateField('operation', $event.target.value)"
        >
          <option value="include">
            {{ t('BROADCASTS.COMPOSER.SOURCE.INCLUDE') }}
          </option>
          <option value="exclude">
            {{ t('BROADCASTS.COMPOSER.SOURCE.EXCLUDE') }}
          </option>
        </select>
      </label>

      <label
        v-if="source.type === 'identifiers'"
        class="flex flex-col gap-1 text-sm font-medium text-n-slate-12"
      >
        {{ t('BROADCASTS.COMPOSER.SOURCE.IDENTIFIER_TYPE') }}
        <select
          :value="source.identifierType"
          class="h-10 rounded-lg border border-n-weak bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
          @change="updateField('identifierType', $event.target.value, true)"
        >
          <option value="telegram_id">
            {{ t('BROADCASTS.COMPOSER.SOURCE.TELEGRAM_ID') }}
          </option>
          <option value="email">
            {{ t('BROADCASTS.COMPOSER.SOURCE.EMAIL') }}
          </option>
          <option value="phone">
            {{ t('BROADCASTS.COMPOSER.SOURCE.PHONE') }}
          </option>
        </select>
      </label>
    </div>

    <div class="flex flex-col gap-2">
      <span class="text-sm font-medium text-n-slate-12">
        {{
          source.type === 'team'
            ? t('BROADCASTS.COMPOSER.SOURCE.TEAMS_REQUIRED')
            : t('BROADCASTS.COMPOSER.SOURCE.TEAMS_OPTIONAL')
        }}
      </span>
      <MultiSelect v-model="selectedTeams" :options="teams" />
      <span
        v-if="source.type === 'identifiers'"
        class="text-xs text-n-slate-10"
      >
        {{ t('BROADCASTS.COMPOSER.SOURCE.TEAM_CONSTRAINT_HELP') }}
      </span>
    </div>

    <template v-if="source.type === 'identifiers'">
      <TextArea
        :model-value="source.rawText"
        :label="t('BROADCASTS.COMPOSER.SOURCE.PASTED_VALUES')"
        :placeholder="t('BROADCASTS.COMPOSER.SOURCE.PASTED_VALUES_PLACEHOLDER')"
        :message="t('BROADCASTS.COMPOSER.SOURCE.PASTED_VALUES_HELP')"
        resize
        min-height="8rem"
        max-height="20rem"
        @update:model-value="updateField('rawText', $event, true)"
      />

      <div class="flex justify-end">
        <Button
          type="button"
          :label="t('BROADCASTS.COMPOSER.SOURCE.CONFIRM')"
          icon="i-lucide-list-checks"
          :is-loading="source.isResolving"
          :disabled="
            !inboxSelected || !hasIdentifierValues || source.isResolving
          "
          @click="emit('confirm')"
        />
      </div>

      <BroadcastResolutionResults
        v-if="source.resolution"
        :resolution="source.resolution"
      />
      <div
        v-else
        class="rounded-lg border border-dashed border-n-strong p-3 text-xs text-n-slate-10"
      >
        {{ t('BROADCASTS.COMPOSER.SOURCE.CONFIRM_REQUIRED') }}
      </div>
    </template>
  </div>
</template>
